import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

let refreshing = null;
api.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config || {};
  const url = cfg.url || "";
  if (error.response?.status === 401 && !cfg._retried && !url.includes("/auth/")) {
    cfg._retried = true;
    refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
    try { await refreshing; return api(cfg); } catch { /* fall through */ }
  }
  return Promise.reject(error);
});

export const formatApiError = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
  return d.msg || String(d);
};

// auth
export const login = (body) => api.post("/auth/login", body).then(r => r.data);
export const logout = () => api.post("/auth/logout").then(r => r.data);
export const me = () => api.get("/auth/me").then(r => r.data);
export const demoLogin = () => api.post("/auth/demo").then(r => r.data);
export const changePassword = (body) => api.post("/auth/change-password", body).then(r => r.data);

// users (admin)
export const listUsers = () => api.get("/users").then(r => r.data);
export const createUser = (body) => api.post("/users", body).then(r => r.data);
export const updateUser = (id, body) => api.patch(`/users/${id}`, body).then(r => r.data);
export const deleteUser = (id) => api.delete(`/users/${id}`).then(r => r.data);

export const listChannels = () => api.get("/channels").then(r => r.data);
export const createChannel = (body) => api.post("/channels", body).then(r => r.data);
export const updateChannel = (id, body) => api.put(`/channels/${id}`, body).then(r => r.data);
export const deleteChannel = (id) => api.delete(`/channels/${id}`).then(r => r.data);

export const listContacts = () => api.get("/contacts").then(r => r.data);
export const createContact = (body) => api.post("/contacts", body).then(r => r.data);
export const deleteContact = (id) => api.delete(`/contacts/${id}`).then(r => r.data);
export const toggleContactOnline = (id) => api.post(`/contacts/${id}/toggle-online`).then(r => r.data);

export const listTransmissions = (params = {}) => api.get("/transmissions", { params }).then(r => r.data);
export const createTransmission = (body) => api.post("/transmissions", body).then(r => r.data);
export const deleteTransmission = (id) => api.delete(`/transmissions/${id}`).then(r => r.data);
export const transmissionAudioUrl = (id) => `${API}/transmissions/${id}/audio`;
export const createBroadcast = (body) => api.post("/transmissions/broadcast", body).then(r => r.data);

export const listAlerts = () => api.get("/alerts").then(r => r.data);
export const createAlert = (body) => api.post("/alerts", body).then(r => r.data);
export const ackAlert = (id) => api.post(`/alerts/${id}/ack`).then(r => r.data);
export const deleteAlert = (id) => api.delete(`/alerts/${id}`).then(r => r.data);

export const simStatus = () => api.get("/simulator/status").then(r => r.data);
export const simToggle = () => api.post("/simulator/toggle").then(r => r.data);

export const listPatches = () => api.get("/patches").then(r => r.data);
export const createPatch = (body) => api.post("/patches", body).then(r => r.data);
export const togglePatch = (id) => api.post(`/patches/${id}/toggle`).then(r => r.data);
export const deletePatch = (id) => api.delete(`/patches/${id}`).then(r => r.data);

export const getSettings = () => api.get("/settings").then(r => r.data);
export const saveSettings = (body) => api.put("/settings", body).then(r => r.data);

export const zelloStatus = () => api.get("/zello/status").then(r => r.data);
export const zelloConnect = () => api.post("/zello/connect").then(r => r.data);
export const zelloDisconnect = () => api.post("/zello/disconnect").then(r => r.data);

export const listTalkgroups = () => api.get("/talkgroups").then(r => r.data);
export const createTalkgroup = (body) => api.post("/talkgroups", body).then(r => r.data);
export const addTalkgroupMembers = (id, member_ids) => api.post(`/talkgroups/${id}/members`, { member_ids }).then(r => r.data);
export const removeTalkgroupMember = (id, contactId) => api.delete(`/talkgroups/${id}/members/${contactId}`).then(r => r.data);
export const deleteTalkgroup = (id) => api.delete(`/talkgroups/${id}`).then(r => r.data);
